/**
 * Global API for CRUD.
 *
 */

import { makeGetRequest, makeFormRequest, makeDeleteRequest } from './helpers';

export const getData = async<R = any> (entityName: string, params: string): Promise<R> => {
    const result = await makeGetRequest(`api/${entityName}${params}`);

    return result.data.result;
}

/**
 * Get  list.
 */
export const getList = async (entityName: string, query: { page: number; items?: number }, sort?: string, filter?: string) => {
    let params = `?page=${query.page}`;
    if (query.items) {
        params += `&items=${query.items}`
    }
    if (sort) {
        params += `&${sort}`;
    }
    if (filter) {
        params += `&${filter}`;
    }

    return getData(entityName, params);
};

/**
 * Get entity by Id.
 */
export const getById = async (entityName: string, id: string, postfix = '') => {
    return getData(entityName, `/${id}${postfix}`);
};

/**
 * Add new entity
 *
 * @param entityName
 * @param data
 */
export const addNewEntity = async (entityName: string, data: Record<string, any>) => {
    const result = await makeFormRequest(`api/${entityName}`, data);

    return result.data;
};

/**
 * remove new entity
 *
 * @param entityName
 * @param id
 */
export const removeEntity = async (entityName: string, id: string | number, postfix = '') => {
    const result = await makeDeleteRequest(`api/${entityName}/${id}${postfix}`);

    return result.data;
};

/**
 * Save entity
 *
 * @param entityName
 * @param id
 * @param data
 * @param postfix
 */
export const updateEntity = async (entityName: string, id: string, data: Record<string, any>, postfix = '/update') => {
    const result = await makeFormRequest(`api/${entityName}/${id}${postfix}`, data);

    return result.data;
};

// SUBMODULES
// for example (doctors/specializations)

type Id = string | number

const createSubmoduleUrl = (
    parentName: string,
    parentId: Id,
    moduleName: string,
): string => `api/${parentName}/${parentId}/${moduleName}`;

/**
 * Create new submodule entity
 *
 * @param entityName
 * @param data
 */
export const createSubmoduleEntity = async (parentName: string, parentId: Id, moduleName: string, data: Record<string, any>) => {
    const url = createSubmoduleUrl(parentName, parentId, moduleName);
    const result = await makeFormRequest(url, data);

    return result.data;
};

/**
 * Read (get) submodule entity by Id.
 */
 export const getSubmoduleById = async (parentName: string, parentId: Id, moduleName: string) => {
    const url = createSubmoduleUrl(parentName, parentId, moduleName);
    const result = await makeGetRequest(url);

    return result.data.result;
};

/**
 * Update submodule entity
 *
 * @param entityName
 * @param id
 * @param data
 */
export const updateSubmoduleEntity = async (parentName: string, parentId: Id, moduleName: string, data: any) => {
    const url = createSubmoduleUrl(parentName, parentId, moduleName);
    const result = await makeFormRequest(`${url}/update`, data);

    return result.data;
};

/**
 * Delete submodule entity
 *
 * @param entityName
 * @param id
 */
export const deleteSubmoduleEntity = async (parentName: string, parentId: Id, moduleName: string, id: Id) => {
    const url = createSubmoduleUrl(parentName, parentId, moduleName);
    // TODO: discuss generalizing api
    const result = await makeDeleteRequest(`${url}?specializationId=${id}`);

    return result.data;
};

export const defineSubmoduleCRUD = (parentName: string, moduleName: string) => {
    return {
        create: (parentId: Id, data: Record<string, any>) => createSubmoduleEntity(parentName, parentId, moduleName, data),
        read: (parentId: Id) => getSubmoduleById(parentName, parentId, moduleName),
        update: (parentId: Id, data: Record<string, any>) => updateSubmoduleEntity(parentName, parentId, moduleName, data),
        delete: (parentId: Id, id: Id) => deleteSubmoduleEntity(parentName, parentId, moduleName, id)
    }
}
